import type { AircraftSpec, FlightType } from "./types";

const FIRST = [
  "James",
  "Margaret",
  "Colin",
  "Siobhan",
  "Andrew",
  "Catherine",
  "Duncan",
  "Elizabeth",
  "Robert",
  "Fiona",
  "Michael",
  "Aileen",
  "Thomas",
  "Moira",
  "Patrick",
  "Sarah",
];

const LAST = [
  "MacNeil",
  "Campbell",
  "Beaton",
  "Fraser",
  "MacDonald",
  "MacLeod",
  "Stewart",
  "Morrison",
  "Cameron",
  "MacKenzie",
  "Walsh",
  "Sullivan",
  "O'Brien",
  "Whitman",
  "Howe",
  "Parker",
];

const COMPANIES = [
  "Cabot Links",
  "Northline Holdings",
  "Scotia Timber",
  "Celtic Aviation Partners",
  "Bras d'Or Marine",
  "Halifax Capital",
  "Atlantic Pulp",
  "Cape Breton Energy",
  "Fundy Partners",
  "North Shore Estates",
];

export function pick<T>(items: readonly T[]): T {
  if (items.length === 0) {
    throw new Error("pick() called with an empty list");
  }
  return items[Math.floor(Math.random() * items.length)] as T;
}

export function person(): string {
  return `${pick(FIRST)} ${pick(LAST)}`;
}

export function people(count: number): string[] {
  const names = new Set<string>();
  let guard = 0;
  while (names.size < count && guard < 40) {
    names.add(person());
    guard += 1;
  }
  return [...names];
}

export function company(): string {
  return pick(COMPANIES);
}

export function paxCountFor(type: FlightType, spec: AircraftSpec): number {
  const max = spec.maxPax;
  switch (type) {
    case "ferry":
      return 0;
    case "medical":
      return 1 + Math.floor(Math.random() * 2);
    case "vip":
      return 1 + Math.floor(Math.random() * Math.min(3, max));
    case "golf":
      return Math.min(max, 2 + Math.floor(Math.random() * 5));
    case "sun":
      return Math.min(max, 2 + Math.floor(Math.random() * 4));
    default:
      return Math.min(max, 1 + Math.floor(Math.random() * 5));
  }
}

export type ScenarioFill = {
  purpose: string;
  scenario: string;
  paxCount: number;
  paxNames: string[];
  isMaintenance: boolean;
  serviceLabel?: string;
};

export function fillScenario(
  type: FlightType,
  originName: string,
  destName: string,
  spec: AircraftSpec,
  serviceLabel?: string,
): ScenarioFill {
  const paxCount = paxCountFor(type, spec);
  const paxNames = paxCount > 0 ? people(paxCount) : [];
  const lead = paxNames[0] ?? person();
  const firm = company();

  if (type === "ferry" || serviceLabel) {
    return {
      purpose: serviceLabel ? `Ferry / ${serviceLabel}` : "Positioning / maintenance",
      scenario: `${spec.name} ${spec.registration} repositions ${originName} to ${destName}${
        serviceLabel ? ` for ${serviceLabel}` : ""
      }. No revenue pax.`,
      paxCount: 0,
      paxNames: [],
      isMaintenance: Boolean(serviceLabel),
      serviceLabel,
    };
  }

  if (type === "golf") {
    return {
      purpose: "Golf inbound — Cabot / Cape Breton",
      scenario: `${lead} + ${Math.max(0, paxCount - 1)} via ${firm}. Inbound ${originName}–${destName} for Cabot Links / Cabot Cliffs. Bags + clubs in the aft bay.`,
      paxCount,
      paxNames,
      isMaintenance: false,
    };
  }

  if (type === "sun") {
    return {
      purpose: "Winter sun / southbound escape",
      scenario: `${lead} party of ${paxCount} leaving ${originName} for ${destName}. Soft bags, no cargo. Return open.`,
      paxCount,
      paxNames,
      isMaintenance: false,
    };
  }

  if (type === "vip") {
    return {
      purpose: "VIP / leisure",
      scenario: `${lead} (${firm}) requests a quiet lift ${originName} to ${destName}. Catering on file. No press.`,
      paxCount,
      paxNames,
      isMaintenance: false,
    };
  }

  if (type === "medical") {
    return {
      purpose: "Air ambulance / medical",
      scenario: `Medevac desk: ${lead} + medical crew ${originName} to ${destName}. Stretcher kit / O2 as required. Priority slot.`,
      paxCount,
      paxNames,
      isMaintenance: false,
    };
  }

  return {
    purpose: "Corporate charter",
    scenario: `${firm} — ${lead} and ${Math.max(0, paxCount - 1)} staff. ${originName} to ${destName}, same-day return possible.`,
    paxCount,
    paxNames,
    isMaintenance: false,
  };
}
